import * as _ from 'lodash';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { ConcentrationLimitValueTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

export type VerificationApproveTablesMode = 'approveDeny' | 'viewOnly';

export const isSanitizedJSON = (test: any): test is Util.SanitizedJSON => {
  if (typeof test === 'object') {
    return true;
  }
  return false;
};

type SanitizedJSONWithNumberValues = {
  finleyValue?: number | null;
  borrowerValue?: number | null;
};

export const isSanitizedJSONWithNumberValues = (
  test: any,
): test is SanitizedJSONWithNumberValues => {
  return (
    isSanitizedJSON(test) &&
    _.has(test, 'finleyValue') &&
    (test.finleyValue === null || _.isNumber(test.finleyValue)) &&
    _.has(test, 'borrowerValue') &&
    (test.borrowerValue === null || _.isNumber(test.borrowerValue))
  );
};

/**
 * In addition to being used for formatting, these functions are used in lieu of an epsilon value
 * to determine floating point equality.
 *
 * The thinking is that the logic that determines whether there is a difference between values
 * should use the same precision as we use when printing values for display.
 */
export const verificationValueTypeToFormatterMap: Record<
  ConcentrationLimitValueTypeGQLType,
  (value: number) => string | undefined
> = {
  currency: (value: number) =>
    commonUtils.numberToCurrencyWithSymbol({ number: value }),
  percentage: (value: number) =>
    commonUtils.numberToFormattedString(value, 'percentage', false),
  number: (value: number) => value.toFixed(2),
};

/**
 * The logic that determines whether there is a difference between values
 * should use the same precision as we use when printing values for display.
 */
export const getBorrowerFinleyDifference = ({
  finleyValue,
  borrowerValue,
  valueType,
}: {
  finleyValue: any;
  borrowerValue: any;
  valueType: ConcentrationLimitValueTypeGQLType;
}): number | undefined => {
  const finleyNumber = typeof finleyValue === 'number' ? finleyValue : 0;
  const borrowerNumber = typeof borrowerValue === 'number' ? borrowerValue : 0;
  const difference = finleyNumber - borrowerNumber;

  const formatFn = verificationValueTypeToFormatterMap[valueType];
  const formattedFinleyValue = formatFn(finleyNumber);
  const formattedBorrowerValue = formatFn(borrowerNumber);
  // Use a string comparison on the formatted value for floating point comparison, in lieu of an
  // epsilon value.
  return difference === 0 || formattedFinleyValue === formattedBorrowerValue
    ? undefined
    : difference;
};
